/* Geometry for `FloatingPopover` (shared.tsx). Kept free of React/DOM so it
   can be unit-tested under vitest's node environment. */

export interface VerticalPlacement {
  top: number;
  /** Set only when the content does not fit on the chosen side. */
  maxHeight?: number;
  /** Opened above the anchor. */
  flipped: boolean;
}

/** Minimum gap kept between a floating popover and the viewport edge. */
export const POPOVER_VIEWPORT_MARGIN = 8;
/** Below this much room, a capped popover is too cramped to use — it is
 *  clamped inside the viewport over the anchor instead so it shows more rows. */
export const POPOVER_MIN_USABLE_HEIGHT = 160;

/**
 * Vertical placement for a viewport-fixed popover of natural height `height`
 * anchored to `anchor`. Prefers below; flips above when it doesn't fit below
 * but does fit above (e.g. a bottom-pinned panel); otherwise caps it to the
 * roomier side with internal scrolling, and as a last resort (both sides
 * cramped) clamps it inside the viewport over the anchor.
 */
export function placeVertically(
  anchor: { top: number; bottom: number },
  height: number,
  viewportHeight: number,
  offsetY: number,
): VerticalPlacement {
  const margin = POPOVER_VIEWPORT_MARGIN;
  const belowTop = anchor.bottom + offsetY;
  const spaceBelow = viewportHeight - belowTop - margin;
  const spaceAbove = anchor.top - offsetY - margin;

  if (height <= spaceBelow) return { top: belowTop, flipped: false };
  if (height <= spaceAbove) return { top: anchor.top - offsetY - height, flipped: true };

  // Fits on neither side — use the roomier one and scroll inside.
  const room = Math.max(spaceAbove, spaceBelow);
  if (room >= POPOVER_MIN_USABLE_HEIGHT) {
    return spaceAbove > spaceBelow
      ? { top: margin, maxHeight: spaceAbove, flipped: true }
      : { top: belowTop, maxHeight: spaceBelow, flipped: false };
  }

  // Both sides cramped (short viewport): clamp inside the viewport, covering
  // the anchor, rather than hanging off-screen.
  const maxHeight = Math.max(0, viewportHeight - 2 * margin);
  const used = Math.min(height, maxHeight);
  return {
    top: Math.max(margin, viewportHeight - margin - used),
    ...(height > maxHeight ? { maxHeight } : {}),
    flipped: false,
  };
}
